import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {number | null} */
  #variantPickerChangeTimeout = null;

  /** @type {MutationObserver | null} */
  #sectionObserver = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {HTMLElement | null} */
  #footerElement = null;

  /** @type {HTMLElement | null} */
  #sectionElement = null;

  connectedCallback() {
    super.connectedCallback();

    if (!this.#sectionElement) {
      this.#sectionElement =
        this.closest('.shopify-section') ??
        (this.dataset.hostSectionId ? document.getElementById(this.dataset.hostSectionId) : null);
    }

    if (this.#sectionElement?.id && !this.dataset.hostSectionId) {
      this.dataset.hostSectionId = this.#sectionElement.id;
    }

    if (this.parentElement !== document.body) {
      document.body.appendChild(this);
      return;
    }

    this.#setupIntersectionObserver();
    this.#setupSectionObserver();

    const { signal } = this.#abortController;
    document.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    document.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });
    document.addEventListener('change', this.#handleVariantPickerChange, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
    this.#sectionObserver?.disconnect();
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    this.#footerElement = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');

    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    if (!this.#targetAddToCartButton) return;

    // Observer acts as a light trigger; actual visibility is decided by scroll position.
    this.#buyButtonsIntersectionObserver = new IntersectionObserver(() => {
      this.#updateStickyVisibility();
    }, {
      threshold: [0, 1],
      rootMargin: '0px 0px -18% 0px',
    });

    this.#buyButtonsIntersectionObserver.observe(this.#targetAddToCartButton);

    const { signal } = this.#abortController;
    window.addEventListener('scroll', this.#handleViewportChange, { signal, passive: true });
    window.addEventListener('resize', this.#handleViewportChange, { signal, passive: true });
    this.#updateStickyVisibility();
  }

  /**
   * Observes the main product section so the sticky bar can mirror actual page changes.
   */
  #setupSectionObserver() {
    if (!this.#sectionElement) return;

    this.#sectionObserver?.disconnect();
    this.#sectionObserver = new MutationObserver(() => {
      this.#queuePageSync();
    });

    this.#sectionObserver.observe(this.#sectionElement, {
      subtree: true,
      childList: true,
      attributes: true,
      characterData: true,
      attributeFilter: ['src', 'srcset', 'value', 'checked', 'selected', 'aria-hidden', 'disabled'],
    });
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    const productForm = this.#getProductForm();
    const productFormElement = productForm?.querySelector('form');
    if (!this.#targetAddToCartButton || !(productFormElement instanceof HTMLFormElement)) return;

    this.#syncVariantToMainForm(productFormElement);
    this.#targetAddToCartButton.dataset.puppet = 'true';
    productFormElement.requestSubmit(this.#targetAddToCartButton);
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    const originSection =
      event.target instanceof HTMLElement ? event.target.closest('.shopify-section') : null;

    if (this.#sectionElement && originSection && originSection !== this.#sectionElement && !event.detail.data.newProduct) {
      return;
    }

    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);

    if (newStickyAddToCart) {
      this.dataset.variantAvailable = newStickyAddToCart.dataset.variantAvailable;
      this.dataset.productId = newStickyAddToCart.dataset.productId || this.dataset.productId;
      this.dataset.productTitle = newStickyAddToCart.dataset.productTitle || this.dataset.productTitle;
      this.dataset.defaultVariantTitle =
        newStickyAddToCart.dataset.defaultVariantTitle || this.dataset.defaultVariantTitle;

      const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
      if (newStickyBar) {
        this.#syncStickyContent(newStickyBar, variant);
      }
    } else {
      this.#syncStickyContentFromPage(variant);
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
      if (this.#buyButtonsIntersectionObserver && this.#targetAddToCartButton) {
        this.#buyButtonsIntersectionObserver.disconnect();
        this.#buyButtonsIntersectionObserver.observe(this.#targetAddToCartButton);
      }
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    this.#updateStickyState();
    this.#queuePageSync(variant);
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    const picker = event.target instanceof HTMLElement ? event.target.closest('variant-picker') : null;
    if (!(picker instanceof HTMLElement)) return;

    const pickerProductId = picker.dataset.productId || '';
    if (pickerProductId && pickerProductId !== this.dataset.productId) return;

    const variantId = this.#getCurrentFormVariantId();
    if (variantId) {
      this.dataset.currentVariantId = variantId.toString();
    }

    window.requestAnimationFrame(() => {
      this.#syncStickyContentFromPage(event.detail.resource ?? null);
    });
  };

  /**
   * Handles direct changes from the variant picker inputs/selects.
   * @param {Event} event
   */
  #handleVariantPickerChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;

    const picker = target.closest('variant-picker');
    if (!(picker instanceof HTMLElement)) return;

    const pickerSection = picker.closest('.shopify-section');
    if (this.#sectionElement && pickerSection && pickerSection !== this.#sectionElement) return;

    const pickerProductId = picker.dataset.productId || '';
    if (pickerProductId && pickerProductId !== this.dataset.productId) return;

    const selectedElement =
      target instanceof HTMLSelectElement ? target.options[target.selectedIndex] : target;
    const nextVariantId = selectedElement?.dataset.variantId || '';
    if (nextVariantId) {
      this.dataset.currentVariantId = nextVariantId;
    }

    if (this.#variantPickerChangeTimeout) {
      clearTimeout(this.#variantPickerChangeTimeout);
    }

    this.#variantPickerChangeTimeout = window.setTimeout(() => {
      this.#queuePageSync({
        id: nextVariantId,
        title: selectedElement?.value || '',
        available: true,
      });
    }, 220);
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
    this.#updateFooterOffset();
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
    stickyBar.style.removeProperty('--sticky-footer-offset');
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.#sectionElement;
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the current variant id from the main product form.
   * @returns {string}
   */
  #getCurrentFormVariantId() {
    const productForm = this.#getProductForm();
    const variantInput = productForm?.querySelector('input[name="id"]');
    return variantInput instanceof HTMLInputElement ? variantInput.value : '';
  }

  /**
   * Queues a follow-up sync after the page has finished reflecting the variant update.
   * @param {ProductVariant | null} variant
   */
  #queuePageSync(variant = null) {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        this.#syncStickyContentFromPage(variant);
      });
    });
  }

  /**
   * Builds a variant label from the current picker state.
   * @returns {string}
   */
  #getSelectedVariantTitleFromPicker() {
    const productId = this.dataset.productId;
    const picker = this.#sectionElement?.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!(picker instanceof HTMLElement)) return '';

    const selectedValues = Array.from(
      picker.querySelectorAll('input:checked, select')
    )
      .map((element) => {
        if (element instanceof HTMLInputElement) return element.value?.trim();
        if (element instanceof HTMLSelectElement) return element.selectedOptions[0]?.textContent?.trim();
        return '';
      })
      .filter(Boolean);

    const defaultVariantTitle = this.dataset.defaultVariantTitle || '';
    const title = selectedValues.join(' / ');
    return title && title !== defaultVariantTitle ? title : '';
  }

  /**
   * Updates sticky footer offset
   */
  #updateStickyState() {
    this.#updateStickyVisibility();
    this.#updateFooterOffset();
  }

  #handleViewportChange = () => {
    this.#updateStickyVisibility();
    this.#updateFooterOffset();
  };

  #updateStickyVisibility() {
    if (!(this.#targetAddToCartButton instanceof HTMLButtonElement)) return;

    const rect = this.#targetAddToCartButton.getBoundingClientRect();
    const showOffset = window.innerHeight * 0.82;
    const visibleBottom = Math.min(rect.bottom, window.innerHeight);
    const visibleTop = Math.max(rect.top, 0);
    const visibleHeight = Math.max(0, visibleBottom - visibleTop);
    const visibleRatio = rect.height > 0 ? visibleHeight / rect.height : 0;
    const shouldShow = rect.top < showOffset && visibleRatio < 0.65;

    if (shouldShow && !this.#isStuck) {
      this.#showStickyBar();
    } else if (!shouldShow && this.#isStuck) {
      this.#hideStickyBar();
    }
  }

  /**
   * Syncs image, title, price, and button state from the active variant/new HTML.
   * @param {Element} newStickyBar
   * @param {ProductVariant | null} variant
   */
  #syncStickyContent(newStickyBar, variant) {
    if (variant?.id) {
      this.dataset.currentVariantId = variant.id.toString();
    }

    const currentImage = this.querySelector('.sticky-add-to-cart__image-img');
    const newImage = newStickyBar.querySelector('.sticky-add-to-cart__image-img');
    if (currentImage instanceof HTMLImageElement && newImage instanceof HTMLImageElement) {
      currentImage.src = newImage.currentSrc || newImage.src;
      currentImage.alt = newImage.alt;
      if (newImage.srcset) {
        currentImage.srcset = newImage.srcset;
      } else {
        currentImage.removeAttribute('srcset');
      }
    }

    const currentPrice = this.querySelector('[data-testid="sticky-price-display"]');
    const newPrice = newStickyBar.querySelector('[data-testid="sticky-price-display"]');
    if (currentPrice instanceof HTMLElement && newPrice instanceof HTMLElement) {
      currentPrice.innerHTML = newPrice.innerHTML;
    }

    const currentTitle = this.querySelector('[data-testid="sticky-product-title"]');
    const newTitle = newStickyBar.querySelector('[data-testid="sticky-product-title"]');
    if (currentTitle instanceof HTMLElement && newTitle instanceof HTMLElement) {
      currentTitle.innerHTML = newTitle.innerHTML;
      const nextBaseTitle = newTitle.querySelector('.sticky-add-to-cart__title-base')?.textContent?.trim();
      if (nextBaseTitle) {
        this.dataset.productTitle = nextBaseTitle;
      }
    }

    const currentVariantLine = this.querySelector('[data-testid="sticky-variant-title"]');
    const newVariantLine = newStickyBar.querySelector('[data-testid="sticky-variant-title"]');
    if (currentVariantLine instanceof HTMLElement && newVariantLine instanceof HTMLElement) {
      currentVariantLine.innerHTML = newVariantLine.innerHTML;
      currentVariantLine.style.display = newVariantLine.style.display;
    }

    this.#syncButtonAvailability(variant);
  }

  /**
   * Fallback sync based on the currently rendered product page.
   * @param {ProductVariant | null} variant
   */
  #syncStickyContentFromPage(variant) {
    if (variant?.id) {
      this.dataset.currentVariantId = variant.id.toString();
    } else {
      const currentVariantId = this.#getCurrentFormVariantId();
      if (currentVariantId) {
        this.dataset.currentVariantId = currentVariantId;
      }
    }

    const currentTitle = this.querySelector('[data-testid="sticky-product-title"]');
    const titleBase = currentTitle?.querySelector('.sticky-add-to-cart__title-base');
    const titleVariant = currentTitle?.querySelector('.sticky-add-to-cart__title-variant');
    const titleSeparator = currentTitle?.querySelector('.sticky-add-to-cart__title-separator');
    const variantLine = this.querySelector('[data-testid="sticky-variant-title"]');
    const pageTitle =
      this.#sectionElement?.querySelector('.product-details h1')?.textContent?.trim() || this.dataset.productTitle || '';

    if (titleBase instanceof HTMLElement && pageTitle) {
      titleBase.textContent = pageTitle;
      this.dataset.productTitle = pageTitle;
    }

    const defaultVariantTitle = this.dataset.defaultVariantTitle || '';
    const nextVariantTitle =
      (variant?.title && variant.title !== defaultVariantTitle ? variant.title : '') || this.#getSelectedVariantTitleFromPicker();

    if (titleVariant instanceof HTMLElement) {
      titleVariant.textContent = nextVariantTitle;
      titleVariant.style.display = nextVariantTitle ? '' : 'none';
    }

    if (titleSeparator instanceof HTMLElement) {
      titleSeparator.style.display = nextVariantTitle ? '' : 'none';
    }

    if (variantLine instanceof HTMLElement) {
      variantLine.textContent = nextVariantTitle;
      variantLine.style.display = nextVariantTitle ? '' : 'none';
    }

    const currentPrice = this.querySelector('[data-testid="sticky-price-display"]');
    const pagePrice = this.#sectionElement?.querySelector('product-price [ref="priceContainer"]');
    if (currentPrice instanceof HTMLElement && pagePrice instanceof HTMLElement) {
      currentPrice.innerHTML = pagePrice.innerHTML;
    }

    const currentImage = this.querySelector('.sticky-add-to-cart__image-img');
    const variantPreviewImage = variant?.featured_media?.preview_image?.src;
    const galleryImage =
      this.#sectionElement?.querySelector('media-gallery .product-media__image') ||
      this.#sectionElement?.querySelector('.product-information__media .product-media__image');

    if (currentImage instanceof HTMLImageElement) {
      if (galleryImage instanceof HTMLImageElement) {
        currentImage.src = galleryImage.currentSrc || galleryImage.src;
        currentImage.alt = galleryImage.alt || pageTitle;
        if (galleryImage.srcset) {
          currentImage.srcset = galleryImage.srcset;
        } else {
          currentImage.removeAttribute('srcset');
        }
      } else if (variantPreviewImage) {
        currentImage.src = variantPreviewImage.includes('width=')
          ? variantPreviewImage
          : `${variantPreviewImage}${variantPreviewImage.includes('?') ? '&' : '?'}width=120`;
        currentImage.alt = variant?.featured_media?.alt || pageTitle;
      }
    }

    this.#syncButtonAvailability(variant);
  }

  /**
   * Ensures the main product form is pointing at the active variant before submit.
   * @param {HTMLFormElement} productFormElement
   */
  #syncVariantToMainForm(productFormElement) {
    const variantInput = productFormElement.querySelector('input[name="id"]');
    if (!(variantInput instanceof HTMLInputElement)) return;

    const activeVariantId =
      this.dataset.currentVariantId ||
      new URL(window.location.href).searchParams.get('variant') ||
      variantInput.value;

    if (activeVariantId) {
      variantInput.value = activeVariantId.toString();
    }
  }

  /**
   * Mirrors the true availability from the main product button when possible.
   * @param {ProductVariant | null} variant
   */
  #syncButtonAvailability(variant) {
    if (!(this.refs.addToCartButton instanceof HTMLButtonElement)) return;

    const mainButtonDisabled =
      this.#targetAddToCartButton instanceof HTMLButtonElement ? this.#targetAddToCartButton.disabled : null;
    const isAvailable = mainButtonDisabled !== null ? !mainButtonDisabled : Boolean(variant && variant.available);

    this.refs.addToCartButton.disabled = !isAvailable;
    this.dataset.variantAvailable = isAvailable ? 'true' : 'false';
  }

  #updateFooterOffset = () => {
    const { stickyBar } = this.refs;
    if (!this.#isStuck || !this.#footerElement) {
      stickyBar.style.removeProperty('--sticky-footer-offset');
      return;
    }

    const footerRect = this.#footerElement.getBoundingClientRect();
    const overlap = Math.max(0, window.innerHeight - footerRect.top);
    const offset = overlap > 0 ? overlap : 0;
    stickyBar.style.setProperty('--sticky-footer-offset', `${offset}px`);
  };
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
